import { Card, CardBody, SimpleGrid } from '@chakra-ui/react';
import { Show } from '@pelicargo/ui';
import { formatPrintedAwb } from '@pelicargo/utils';

import { useCurrentDraft } from '../../../hooks/useCurrentDraft';
import { Field } from './Field';
import { ApplySummary, BookingConfirmationPayload, formatCutoff } from './payload';

type Props = {
  booking: BookingConfirmationPayload | null;
  applied: ApplySummary | null;
};

/**
 * What the deterministic matcher resolved this email to: request, carrier, quote, AWB — as the email stated them. A
 * column the apply wrote onto the quote is marked; the check against the quote's own value is on its flight strip.
 */
export const LinkageCard = ({ booking, applied }: Props) => {
  const { draft } = useCurrentDraft();
  const isGuessedQuote = booking?.quoteLinkage === 'guessed';

  const requestId = draft?.request_id;
  const quoteId = draft?.quote_id;
  const requestNumber = requestId ? `R${requestId}` : null;
  const requestPath = requestId ? `/requests/${requestId}/airline` : undefined;
  const quoteLabel = isGuessedQuote ? 'Quote (best guess)' : 'Quote';
  const quoteNumber = quoteId ? `Q${quoteId}` : null;
  const quotePath = quoteId && requestPath ? `${requestPath}#Q${quoteId}` : undefined;
  const cutoff = booking?.cutoff ? formatCutoff(booking.cutoff) : null;

  const isAwbFilled = !!applied?.quoteFilled.has('awb_number');
  const isCutoffFilled = !!applied?.quoteFilled.has('cutoff_at');
  const isDeliveryAddressFilled = !!applied?.quoteFilled.has('delivery_address');

  const showCutoff = !!booking?.cutoff;
  const showDeliveryAddress = !!booking?.deliveryAddress;

  return (
    <Card variant="outline">
      <CardBody>
        <SimpleGrid columns={{ base: 2, md: 4 }} spacing="4">
          <Field label="Request" value={requestNumber} to={requestPath} />
          <Field label="Carrier" value={draft?.provider_id} />
          <Field label={quoteLabel} value={quoteNumber} to={quotePath} />
          <Field label="AWB" value={formatPrintedAwb(booking?.awb)} isFilled={isAwbFilled} />
          <Show if={showCutoff}>
            <Field label="Cut-off" value={cutoff} isFilled={isCutoffFilled} />
          </Show>
          <Show if={showDeliveryAddress}>
            <Field label="Delivery address" value={booking?.deliveryAddress} isFilled={isDeliveryAddressFilled} />
          </Show>
        </SimpleGrid>
      </CardBody>
    </Card>
  );
};
